import { useBilling } from "@/hooks/useBilling";
import { usePaywall } from "@/hooks/usePaywall";
import { PricingCards } from "@/components/billing/PricingCards";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { CreditCard, ExternalLink, FileText, BarChart3, Clock, AlertTriangle, Shield } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { useToast } from "@/hooks/use-toast";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { RoleGate } from "@/components/RoleGate";
import { events as analyticsEvents } from "@/lib/analytics";

function formatLimit(v: number | null) {
  if (v === null) return "∞";
  return v.toLocaleString("pt-BR");
}

function usagePct(used: number, limit: number | null) {
  if (!limit) return 0;
  return Math.min(100, Math.round((used / limit) * 100));
}

function statusBadge(status: string) {
  const map: Record<string, string> = {
    active: "Ativo",
    past_due: "Pagamento Pendente",
    canceled: "Cancelado",
    trialing: "Trial",
    suspended: "Suspenso",
  };
  const variant = status === "active" ? "default" : status === "trialing" ? "secondary" : "destructive";
  return <Badge variant={variant as any}>{map[status] || status}</Badge>;
}

export default function BillingPage() {
  const { user } = useAuth();
  const { toast } = useToast();
  const { data: paywallStatus } = usePaywall();

  const { data: orgMember } = useQuery({
    queryKey: ["user-org", user?.id],
    queryFn: async () => {
      if (!user?.id) return null;
      const { data } = await supabase
        .from("org_members")
        .select("org_id")
        .eq("user_id", user.id)
        .limit(1)
        .maybeSingle();
      return data;
    },
    enabled: !!user?.id,
  });

  const orgId = orgMember?.org_id;
  const {
    plans, subscription, invoices, usage, isLoading, createCheckout, openBillingPortal,
  } = useBilling(orgId);

  const currentPlan = subscription?.billing_plans || plans.find(p => p.name === (usage?.plan || "free"));

  const handleCheckout = async (planId: string) => {
    try {
      await createCheckout(planId);
      const plan = plans.find(p => p.id === planId);
      if (plan) analyticsEvents.billingUpgraded(plan.name);
    } catch (e: any) {
      toast({ title: "Erro ao iniciar checkout", description: e.message, variant: "destructive" });
    }
  };

  const handlePortal = async () => {
    try {
      await openBillingPortal();
    } catch (e: any) {
      toast({ title: "Erro ao abrir portal", description: e.message, variant: "destructive" });
    }
  };

  return (
    <div className="space-y-6 max-w-6xl">
      <h1 className="text-2xl font-bold text-foreground">Billing & Planos</h1>

      {/* Trial Countdown */}
      {paywallStatus?.billingStatus === "trialing" && paywallStatus.trialDaysLeft !== null && (
        <Alert variant={paywallStatus.trialDaysLeft <= 3 ? "destructive" : "default"}>
          <Clock className="h-4 w-4" />
          <AlertDescription className="flex items-center justify-between gap-4">
            <span>
              {paywallStatus.trialDaysLeft === 0
                ? "Seu trial expira hoje! Escolha um plano para continuar."
                : `Seu trial termina em ${paywallStatus.trialDaysLeft} dia(s). Escolha um plano para não perder acesso.`}
            </span>
          </AlertDescription>
        </Alert>
      )}

      {/* Grace Period Warning */}
      {paywallStatus?.billingStatus === "past_due" && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription className="flex items-center justify-between gap-4">
            <span>
              Pagamento falhou. Você tem {paywallStatus.graceDaysLeft ?? 0} dia(s) para atualizar antes da suspensão.
            </span>
            <Button size="sm" variant="destructive" onClick={handlePortal}>
              Atualizar Pagamento
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {/* Suspended Warning */}
      {paywallStatus?.billingStatus === "suspended" && (
        <Alert variant="destructive">
          <Shield className="h-4 w-4" />
          <AlertDescription className="flex items-center justify-between gap-4">
            <span>Conta suspensa. Atualize seu pagamento para reativar todas as funcionalidades.</span>
            <Button size="sm" variant="destructive" onClick={handlePortal}>
              Reativar Conta
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {/* Current Plan & Usage */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <CreditCard className="w-5 h-5" /> Plano Atual
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-2xl font-bold capitalize">{currentPlan?.name || usage?.plan || "Free"}</span>
              {paywallStatus && statusBadge(paywallStatus.billingStatus)}
            </div>
            {currentPlan?.price_brl !== undefined && (
              <p className="text-muted-foreground">
                R$ {(currentPlan.price_brl / 100).toFixed(2)}/mês
              </p>
            )}
            {subscription?.current_period_end && (
              <p className="text-sm text-muted-foreground">
                Próxima renovação: {format(new Date(subscription.current_period_end), "dd/MM/yyyy", { locale: ptBR })}
              </p>
            )}
            <div className="flex gap-2 mt-2">
              <RoleGate permission="org.billing">
                {subscription && (
                  <Button variant="outline" size="sm" onClick={handlePortal}>
                    <ExternalLink className="w-4 h-4 mr-1" /> Gerenciar Assinatura
                  </Button>
                )}
              </RoleGate>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <BarChart3 className="w-5 h-5" /> Uso do Mês
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span>Mensagens</span>
                <span className="text-muted-foreground">
                  {(usage?.messages_used ?? 0).toLocaleString("pt-BR")} / {formatLimit(usage?.monthly_message_limit ?? null)}
                </span>
              </div>
              <Progress value={usagePct(usage?.messages_used ?? 0, usage?.monthly_message_limit ?? null)} className="h-2" />
            </div>
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span>Chamadas IA</span>
                <span className="text-muted-foreground">
                  {(usage?.ai_calls_used ?? 0).toLocaleString("pt-BR")} / {formatLimit(usage?.monthly_ai_calls_limit ?? null)}
                </span>
              </div>
              <Progress value={usagePct(usage?.ai_calls_used ?? 0, usage?.monthly_ai_calls_limit ?? null)} className="h-2" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Pricing Cards */}
      <div>
        <h2 className="text-lg font-semibold mb-4">Planos Disponíveis</h2>
        <PricingCards
          plans={plans}
          currentPlanName={currentPlan?.name || usage?.plan}
          onSelectPlan={handleCheckout}
          stripeConfigured={true}
        />
      </div>

      {/* Invoices */}
      {invoices.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <FileText className="w-5 h-5" /> Histórico de Faturas
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {invoices.map((inv) => (
                <div key={inv.id} className="flex items-center justify-between py-2 border-b last:border-0">
                  <div className="space-y-0.5">
                    <p className="text-sm font-medium">
                      R$ {(inv.amount_cents / 100).toFixed(2)}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {inv.period_start && inv.period_end
                        ? `${format(new Date(inv.period_start), "dd/MM/yy")} - ${format(new Date(inv.period_end), "dd/MM/yy")}`
                        : format(new Date(inv.created_at), "dd/MM/yyyy")}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant={inv.status === "paid" ? "default" : "secondary"}>
                      {inv.status === "paid" ? "Pago" : inv.status === "open" ? "Aberto" : inv.status}
                    </Badge>
                    {inv.invoice_url && (
                      <Button variant="ghost" size="sm" asChild>
                        <a href={inv.invoice_url} target="_blank" rel="noopener noreferrer">
                          <ExternalLink className="w-4 h-4" />
                        </a>
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
